import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, Trophy } from 'lucide-react';
import { formatStat } from '@/lib/sportStats';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Sport-aware season leaderboard. Renders one board per tracked stat
 * (Goals, Assists, Kills, Aces, …) so users can immediately see who is
 * leading each category. Public read — no auth needed for public events.
 */
export function SportLeaderboard({ eventId, refreshKey }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!eventId) return;
    setLoading(true);
    fetch(`${API_URL}/api/events/${eventId}/leaderboard`)
      .then((r) => r.json())
      .then((d) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [eventId, refreshKey]);

  if (loading) {
    return (
      <div className="py-12 text-center text-slate-500 flex items-center justify-center gap-2">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading leaderboard…
      </div>
    );
  }

  const definition = data?.sport_definition;
  const boards = data?.boards || {};
  const stats = definition?.stats || [];
  const hasAnyEntries = stats.some((s) => (boards[s.key] || []).length > 0);

  if (!definition || stats.length === 0) {
    return (
      <Card className="border-slate-200">
        <CardContent className="py-10 text-center text-slate-500">
          This sport doesn&apos;t have per-player stat tracking.
        </CardContent>
      </Card>
    );
  }

  if (!hasAnyEntries) {
    return (
      <Card className="border-slate-200">
        <CardContent className="py-10 text-center">
          <Trophy className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-600 font-medium">No stats yet</p>
          <p className="text-sm text-slate-500 mt-1">
            Open any game and click the Box Score button to start tracking.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sport-leaderboard">
      {stats.map((s) => {
        const rows = boards[s.key] || [];
        if (rows.length === 0) return null;
        return (
          <Card key={s.key} className="border-slate-200" data-testid={`leaderboard-${s.key}`}>
            <CardContent className="py-4">
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="w-4 h-4 text-[#00247D]" />
                <h3 className="font-semibold text-slate-800">{s.label} Leaders</h3>
              </div>
              <ol className="space-y-2 text-sm">
                {rows.slice(0, 10).map((r, i) => (
                  <li
                    key={`${r.name}-${i}`}
                    className="flex items-baseline justify-between gap-2 border-b border-slate-100 last:border-b-0 pb-1.5"
                  >
                    <div className="min-w-0 flex-1">
                      <span className="text-slate-400 font-medium mr-2">{i + 1}.</span>
                      <span className="font-medium text-slate-800">{r.name}</span>
                      {r.team_name ? (
                        <span className="text-slate-500 text-xs ml-2 truncate">{r.team_name}</span>
                      ) : null}
                    </div>
                    <span className="font-mono font-semibold text-[#00247D] tabular-nums">{formatStat(s.key, r.value)}</span>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
